import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { translateErrorParts } from '../lib/translate-error';

/** Data + derived geometry for the Google Calendar week preview. The panel
 *  component is left as pure rendering: it reads days/events/blocks/formatters
 *  from here and lays them out. Backed by the `googleCalendarEvents` procedure
 *  (events.list). Read only; prev/next shift the window by 7 days. */

export const HOUR_PX = 44;
const DEFAULT_START_HOUR = 6;
const DEFAULT_END_HOUR = 20;

export type CalEvent = {
  id: string;
  summary: string | null;
  description: string | null;
  location: string | null;
  start: Date;
  end: Date;
  allDay: boolean;
  status: string | null;
  htmlLink: string | null;
};

export type EventBlock = {
  key: string;
  top: number;
  height: number;
  title: string;
  tip: string;
  /** Optional per-block color classes (border + bg). Falls back to primary. */
  color?: string;
  /** Column-packing for overlapping events: this block's column index and the
   *  number of columns in its overlap cluster. Absent (→ full width) when a
   *  producer doesn't compute lanes (e.g. the Google week preview). */
  lane?: number;
  laneCount?: number;
};

/** Monday 00:00 (local) of the week `offsetWeeks` away from today. */
function startOfWeek(offsetWeeks: number): Date {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const mondayIndex = (d.getDay() + 6) % 7; // getDay: 0=Sun..6=Sat → 0=Mon..6=Sun
  d.setDate(d.getDate() - mondayIndex + offsetWeeks * 7);
  return d;
}

export function useGoogleCalendarWeek(sourceId: string, kind: 'google' | 'local' = 'google') {
  const { t, i18n } = useTranslation();
  const crud = useClientQueries(schema);
  const [weekOffset, setWeekOffset] = useState(0);

  const weekStart = useMemo(() => startOfWeek(weekOffset), [weekOffset]);
  const weekEnd = useMemo(() => {
    const e = new Date(weekStart);
    e.setDate(e.getDate() + 7);
    return e;
  }, [weekStart]);

  const queryArgs = {
    args: {
      input: {
        availabilitySourceId: sourceId,
        from: weekStart.toISOString(),
        to: weekEnd.toISOString(),
      },
    },
  };
  // Two sources, one shape. Only the matching `kind` query is enabled — the
  // other stays idle (both hooks are still called, per the rules of hooks). The
  // LOCAL procedure returns working-hours ranges + booked appts (status
  // "working"/"busy"), which only the LOCAL styling below reacts to.
  const googleQ = crud.$procs.googleCalendarEvents.useQuery(queryArgs, {
    staleTime: 30_000,
    retry: false,
    enabled: kind === 'google',
  });
  const localQ = crud.$procs.localCalendarPreview.useQuery(queryArgs, {
    staleTime: 30_000,
    retry: false,
    enabled: kind === 'local',
  });
  const q = kind === 'local' ? localQ : googleQ;

  const events: CalEvent[] = useMemo(() => {
    type RawEvent = Omit<CalEvent, 'start' | 'end'> & { start: string; end: string };
    const raw = (q.data as { events?: RawEvent[] } | undefined)?.events ?? [];
    return raw.map((e) => ({ ...e, start: new Date(e.start), end: new Date(e.end) }));
  }, [q.data]);

  const timedEvents = useMemo(() => events.filter((e) => !e.allDay), [events]);
  const allDayEvents = useMemo(() => events.filter((e) => e.allDay), [events]);

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [weekStart],
  );

  // Visible hour window: default 08–20, expanded to cover any timed event.
  const [startHour, endHour] = useMemo(() => {
    let lo = DEFAULT_START_HOUR;
    let hi = DEFAULT_END_HOUR;
    for (const e of timedEvents) {
      lo = Math.min(lo, e.start.getHours());
      hi = Math.max(hi, e.end.getHours() + (e.end.getMinutes() > 0 ? 1 : 0));
    }
    return [Math.max(0, lo), Math.min(24, Math.max(hi, lo + 1))];
  }, [timedEvents]);

  const hours = useMemo(
    () => Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i),
    [startHour, endHour],
  );
  const gridHeight = (endHour - startHour) * HOUR_PX;

  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }),
    [i18n.language],
  );
  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { weekday: 'short', day: 'numeric' }),
    [i18n.language],
  );
  const rangeFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }),
    [i18n.language],
  );
  const listDayFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { weekday: 'long', day: 'numeric', month: 'short' }),
    [i18n.language],
  );

  const title = (e: CalEvent) =>
    e.status === 'working'
      ? t('specialistConfig.sources.local.weekPreview.working')
      : e.summary || t('specialistConfig.sources.google.weekPreview.noTitle');

  // LOCAL preview colours by status: working-hours ranges as a soft band, booked
  // appointments in the destructive tint on top. Google events (no such status)
  // fall through to the default primary tint — unchanged.
  const blockColor = (e: CalEvent): string | undefined =>
    e.status === 'busy'
      ? 'border-destructive/50 bg-destructive/25'
      : e.status === 'working'
        ? 'border-primary/30 bg-primary/10'
        : undefined;

  /** Timed-event blocks clipped to one day's visible window, positioned in px. */
  function blocksForDay(day: Date): EventBlock[] {
    const dayTop = new Date(day);
    dayTop.setHours(startHour, 0, 0, 0);
    const dayBottom = new Date(day);
    dayBottom.setHours(0, 0, 0, 0);
    dayBottom.setHours(endHour, 0, 0, 0); // endHour=24 rolls to next midnight — intended
    return timedEvents.flatMap((e) => {
      const s = Math.max(e.start.getTime(), dayTop.getTime());
      const en = Math.min(e.end.getTime(), dayBottom.getTime());
      if (en <= s) return [];
      const top = ((s - dayTop.getTime()) / 3_600_000) * HOUR_PX;
      const height = ((en - s) / 3_600_000) * HOUR_PX;
      return [
        {
          key: `${e.id}-${day.getDate()}`,
          top,
          height: Math.max(height, 14),
          title: title(e),
          tip: `${timeFmt.format(e.start)}–${timeFmt.format(e.end)} ${title(e)}${e.location ? ` · ${e.location}` : ''}`,
          color: blockColor(e),
        },
      ];
    });
  }

  return {
    weekOffset,
    setWeekOffset,
    days,
    events,
    allDayEvents,
    startHour,
    hours,
    gridHeight,
    timeFmt,
    dayFmt,
    rangeFmt,
    listDayFmt,
    title,
    blocksForDay,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    refetch: q.refetch,
    errParts: q.isError ? translateErrorParts(t, q.error) : null,
  };
}

/** Everything the presentational calendar parts (header / grid / list) read. */
export type CalendarWeek = ReturnType<typeof useGoogleCalendarWeek>;
